// Centralised structured-data (JSON-LD) builders for the site.
// Keeps SEO schemas in one place so they stay consistent across pages.
import { SITE_URL } from "@/lib/site";

const BASE_URL = SITE_URL;
const PHONE_E164 = "+39 06 9763 6222";

const BASE_FAQS = [
  {
    q: "Quanto costa il carro attrezzi a Roma?",
    a: "Il costo del carro attrezzi a Roma dipende dalla distanza, dall'orario e dal tipo di intervento (traino, recupero auto incidentate, recupero moto). Forniamo sempre un preventivo chiaro e trasparente al telefono, senza costi nascosti. Chiama il 06 9763 6222 per un prezzo immediato.",
  },
  {
    q: "Quanto tempo impiegate ad arrivare?",
    a: "Operiamo H24 in tutta Roma e provincia con tempi di intervento molto ridotti. In zone come EUR, Ostia, Fiumicino, Tiburtina, Aurelia o Appia organizziamo l'arrivo del carro attrezzi più vicino alla tua posizione per ridurre l'attesa al minimo.",
  },
  {
    q: "Lavorate anche di notte e nei festivi?",
    a: "Sì, il nostro servizio di carro attrezzi e soccorso stradale è operativo 24 ore su 24, 7 giorni su 7, festività incluse. Notte, weekend, Ferragosto o Natale: chiamaci al 06 9763 6222 in qualsiasi momento.",
  },
  {
    q: "Accettate pagamenti con carta?",
    a: "Sì, accettiamo contanti e pagamenti con carta direttamente sul posto tramite POS mobili. Nessun pagamento anticipato richiesto.",
  },
  {
    q: "Recuperate auto in situazioni difficili come ribaltamenti, auto infossate o incastrate nella sabbia?",
    a: "Sì. Interveniamo anche in situazioni complesse: auto ribaltate o su un fianco dopo un incidente, veicoli infossati in terreno cedevole o fango, auto incastrate nella sabbia a Ostia, Fregene e Fiumicino, oppure ferme per blocco centralina o guasto elettronico. Descrivici la situazione al telefono e arriviamo con il mezzo giusto.",
  },
];

export function localBusinessSchema({ extraAreas = [] } = {}) {
  const areaServed = [
    { "@type": "City", name: "Roma" },
    { "@type": "AdministrativeArea", name: "Provincia di Roma" },
    { "@type": "Place", name: "EUR Roma" },
    { "@type": "Place", name: "Ostia" },
    { "@type": "Place", name: "Fiumicino" },
    ...extraAreas.map((n) => ({ "@type": "Place", name: n })),
  ];

  return {
    "@context": "https://schema.org",
    "@type": ["AutomotiveBusiness", "LocalBusiness"],
    "@id": `${BASE_URL}/#business`,
    name: "Carroattrezzi Roma H24",
    alternateName: ["Carroassistenza Roma", "Soccorso Stradale Roma H24"],
    url: `${BASE_URL}/`,
    image: [`${BASE_URL}/og-image.png`],
    logo: `${BASE_URL}/logo-mark.png`,
    description:
      "Servizio di carro attrezzi e soccorso stradale H24 a Roma e provincia. Recupero auto incidentate, traino veicoli, auto in panne, batteria scarica, recupero moto, assistenza notturna, recupero auto ribaltate, infossate o incastrate nella sabbia e interventi su auto ferme per blocco centralina.",
    telephone: PHONE_E164,
    priceRange: "€€",
    currenciesAccepted: "EUR",
    paymentAccepted: "Cash, Credit Card",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Roma",
      addressRegion: "RM",
      postalCode: "00100",
      addressCountry: "IT",
    },
    areaServed,
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
    geo: { "@type": "GeoCoordinates", latitude: "41.9028", longitude: "12.4964" },
    hasMap: "https://www.google.com/maps?q=Roma",
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: PHONE_E164,
        contactType: "emergency",
        areaServed: "IT",
        availableLanguage: ["it"],
        hoursAvailable: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
          ],
          opens: "00:00",
          closes: "23:59",
        },
      },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "11",
      bestRating: "5",
      worstRating: "1",
    },
    makesOffer: [
      "Carro attrezzi H24 Roma",
      "Soccorso stradale Roma",
      "Recupero auto incidentate Roma",
      "Recupero moto Roma",
      "Traino veicoli Roma",
      "Avviamento batteria scarica Roma",
      "Trasporto auto Roma",
      "Assistenza stradale notturna Roma",
      "Recupero auto ribaltate Roma",
      "Recupero auto infossate Roma",
      "Recupero auto incastrate nella sabbia (Ostia, Fregene, Fiumicino)",
      "Auto ferma per blocco centralina Roma",
    ].map((name) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name },
    })),
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url: `${BASE_URL}/`,
    name: "Carroattrezzi Roma H24",
    inLanguage: "it-IT",
    publisher: { "@id": `${BASE_URL}/#business` },
  };
}

export function breadcrumbSchema(items) {
  // items: [{ name, url }]
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function faqPageSchema(faqs = BASE_FAQS) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function serviceSchema({ name, description, url, areaServed = "Roma" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url,
    provider: { "@id": `${BASE_URL}/#business` },
    areaServed: { "@type": "City", name: areaServed },
    serviceType: "Soccorso stradale",
    availableChannel: {
      "@type": "ServiceChannel",
      servicePhone: PHONE_E164,
      serviceUrl: url,
    },
  };
}

export const BASE_URL_EXPORT = BASE_URL;
